import React, { useState, useEffect } from 'react';
import { Plus, X, Sparkles } from 'lucide-react';
import { benefitsService } from '../../services/benefits';
import { subscriptionService } from '../../services/subscription';
import { benefitDiscountPct } from '../../lib/utils';

const fmt = (n) => `$${Math.round(n).toLocaleString('es-MX')}`;

// Herramienta de venta en piso: el closer captura cuánto piensa gastar el
// cliente en cada tienda y muestra el ahorro contra el costo del acceso.
const SavingsCalculator = () => {
  const [benefits, setBenefits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([{ benefitId: '', amount: '' }]);

  useEffect(() => {
    Promise.all([
      benefitsService.getAll().catch(() => null),
      subscriptionService.getPlans().catch(() => null),
    ])
      .then(([benefitsRes, plansRes]) => {
        if (benefitsRes?.success && Array.isArray(benefitsRes.data)) {
          // Solo beneficios con % de descuento: son los que producen un ahorro
          // calculable. Un 2x1 o un regalo no se puede convertir a monto aquí.
          const conPct = benefitsRes.data
            .map((b) => ({ ...b, pct: benefitDiscountPct(b) }))
            .filter((b) => b.pct > 0);

          // Varias tiendas tienen el mismo beneficio cargado dos veces; en el
          // selector se vería la misma opción repetida. Dos porcentajes
          // distintos de una misma tienda sí son ofertas distintas y se conservan.
          const vistos = new Set();
          const unicos = conPct.filter((b) => {
            const clave = `${b.storeName}|${b.pct}`;
            if (vistos.has(clave)) return false;
            vistos.add(clave);
            return true;
          });

          setBenefits(
            unicos.sort((a, b) => (a.storeName || '').localeCompare(b.storeName || ''))
          );
        }
        if (plansRes?.success) setPlans(plansRes.data.plans || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const updateRow = (index, field, value) => {
    setRows(rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows([...rows, { benefitId: '', amount: '' }]);
  const removeRow = (index) => setRows(rows.filter((_, i) => i !== index));

  const rowSaving = (row) => {
    const benefit = benefits.find((b) => b.id === row.benefitId);
    const amount = parseFloat(row.amount);
    if (!benefit || !amount || amount <= 0) return 0;
    return (amount * benefit.pct) / 100;
  };

  const totalSpend = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalSaving = rows.reduce((sum, r) => sum + rowSaving(r), 0);

  const annual = plans.find((p) => p.interval === 'year');
  const monthly = plans.find((p) => p.interval === 'month');

  // El anual es el plan que se busca cerrar; el mensual es el escalón de entrada.
  const target = annual || monthly;
  const targetPrice = target?.amount || 0;
  const recovers = targetPrice > 0 && totalSaving >= targetPrice;
  const net = totalSaving - targetPrice;
  const progress = targetPrice > 0 ? Math.min(100, (totalSaving / targetPrice) * 100) : 0;
  // Si aún no llega al anual pero ya cubre el mensual, sirve como cierre alterno.
  const coversMonthly = monthly && !recovers && totalSaving >= monthly.amount;

  const card = {
    background: '#fff',
    borderRadius: 20,
    border: '1px solid rgba(0,0,0,0.06)',
    padding: 18,
  };

  const inputStyle = {
    width: '100%',
    height: 46,
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.1)',
    padding: '0 12px',
    fontSize: 15,
    color: '#0A0A0A',
    background: '#fff',
    outline: 'none',
  };

  return (
    <div style={{ padding: '8px 20px 20px' }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 19, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em' }}>
          Calculadora de ahorro
        </h2>
        <p style={{ fontSize: 13.5, color: '#6B7280', marginTop: 3 }}>
          Pregúntale cuánto piensa gastar hoy y en qué tiendas.
        </p>
      </div>

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
          Cargando beneficios...
        </div>
      ) : benefits.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
          No hay beneficios con porcentaje de descuento configurados.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((row, index) => {
              const saving = rowSaving(row);
              return (
                <div key={index} style={card}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <select
                      value={row.benefitId}
                      onChange={(e) => updateRow(index, 'benefitId', e.target.value)}
                      style={{ ...inputStyle, flex: 1, appearance: 'none' }}
                    >
                      <option value="">Elige una tienda</option>
                      {benefits.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.storeName} · {b.pct}%
                        </option>
                      ))}
                    </select>
                    {rows.length > 1 && (
                      <button
                        onClick={() => removeRow(index)}
                        style={{
                          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                          border: '1px solid rgba(0,0,0,0.08)', background: '#fff', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        <X size={15} color="#9CA3AF" />
                      </button>
                    )}
                  </div>

                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={row.amount}
                    onChange={(e) => updateRow(index, 'amount', e.target.value)}
                    placeholder="¿Cuánto piensa gastar? (MXN)"
                    style={inputStyle}
                  />

                  {saving > 0 && (
                    <div style={{ marginTop: 10, fontSize: 14, color: '#0A0A0A', fontWeight: 600 }}>
                      Se ahorra {fmt(saving)} aquí
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={addRow}
            style={{
              width: '100%', marginTop: 10, height: 46, borderRadius: 14,
              border: '1px dashed rgba(0,0,0,0.18)', background: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontSize: 14.5, fontWeight: 600, color: '#6B7280',
            }}>
            <Plus size={16} /> Agregar otra tienda
          </button>

          {/* Resultado */}
          <div style={{
            marginTop: 16, borderRadius: 24, padding: 22,
            background: recovers ? '#0A0A0A' : '#fff',
            border: recovers ? 'none' : '1px solid rgba(0,0,0,0.06)',
            color: recovers ? '#fff' : '#0A0A0A',
          }}>
            <div style={{ fontSize: 13, opacity: 0.65, fontWeight: 500 }}>Ahorro total de hoy</div>
            <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
              {fmt(totalSaving)}
            </div>
            <div style={{ fontSize: 13.5, opacity: 0.65, marginTop: 2 }}>
              Sobre una compra de {fmt(totalSpend)}
            </div>

            {/* Progreso hacia recuperar el costo del acceso */}
            <div style={{
              marginTop: 16, height: 6, borderRadius: 9999,
              background: recovers ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.07)',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${progress}%`, height: '100%', borderRadius: 9999,
                background: recovers ? '#fff' : '#0A0A0A',
                transition: 'width .3s ease',
              }} />
            </div>

            <div style={{
              marginTop: 14, paddingTop: 14,
              borderTop: `1px solid ${recovers ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)'}`,
              fontSize: 14.5, lineHeight: 1.5, fontWeight: 600,
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              {recovers && <Sparkles size={17} style={{ flexShrink: 0, marginTop: 1 }} />}
              <span>
                {totalSaving === 0
                  ? `El acceso anual cuesta ${fmt(targetPrice)}. Captura su compra para ver cuánto se ahorra.`
                  : recovers
                    ? `Recupera los ${fmt(targetPrice)} del acceso anual con esta sola compra y todavía le sobran ${fmt(net)}.`
                    : coversMonthly
                      ? `Ya recupera el plan mensual de ${fmt(monthly.amount)} con esta compra. Le faltan ${fmt(targetPrice - totalSaving)} para cubrir el año completo.`
                      : `Le faltan ${fmt(targetPrice - totalSaving)} de ahorro para recuperar el acceso anual. Agrega otra tienda.`}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SavingsCalculator;
